import axios from "axios";
import { TOKEN_KEYS } from "../components/common/Util/Util";

import CryptoJS from "crypto-js";
import { toast } from "react-toastify";

const SECRET_KEY = "Nwjy3ghs1d1lrFryuUpWsg==";

// Convert File to Base64
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve({
        filename: file.name,
        contentType: file.type,
        content: reader.result.split(",")[1], // remove data:*/*;base64,
      });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Encrypt standard JS object
const encryptData = (data) => {
  try {
    const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
    const iv = CryptoJS.lib.WordArray.random(16);
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key, { iv });
    return {
      data: encrypted.toString(),
      iv: CryptoJS.enc.Base64.stringify(iv),
    };
  } catch (error) {
    console.error("Encryption Error:", error);
    return data;
  }
};

// Decrypt backend AES-encrypted response
const decryptData = (encryptedResponse) => {
  try {
    const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
    const iv = CryptoJS.enc.Base64.parse(encryptedResponse.iv);
    const ciphertext = CryptoJS.enc.Base64.parse(encryptedResponse.data);
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext },
      key,
      { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );
    return JSON.parse(CryptoJS.enc.Utf8.stringify(decrypted));
  } catch (error) {
    console.error("Decryption failed:", error.message);
    throw new Error("Decryption failed. Please verify encryption keys and IV.");
  }
};

// Encrypt query parameters
const encryptUrlParams = (url) => {
  const [baseUrl, queryString] = url.split("?");
  if (!queryString) return url;

  const params = new URLSearchParams(queryString.trim());
  const encryptedParams = new URLSearchParams();

  params.forEach((value, key) => {
    const encrypted = encryptData(value);
    encryptedParams.append(key, encrypted.data);
    encryptedParams.append(`${key}_iv`, encrypted.iv);
  });

  return `${baseUrl}?${encryptedParams.toString()}`;
};

// Encrypt FormData (files + fields)
const encryptFormData = async (formData) => {
  const plainObj = {};
  const filePromises = [];

  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      filePromises.push(
        fileToBase64(value).then((fileData) => {
          // ✅ Use fileData directly
          if (plainObj[key]) {
            if (!Array.isArray(plainObj[key])) {
              plainObj[key] = [plainObj[key]];
            }
            plainObj[key].push(fileData);
          } else {
            plainObj[key] = [fileData]; // always an array
          }
        })
      );
    } else {
      if (plainObj[key]) {
        if (!Array.isArray(plainObj[key])) {
          plainObj[key] = [plainObj[key]];
        }
        plainObj[key].push(value);
      } else {
        plainObj[key] = value;
      }
    }
  }

  await Promise.all(filePromises);

  const encrypted = encryptData(plainObj);
  const encryptedForm = new FormData();
  encryptedForm.append("data", encrypted.data);
  encryptedForm.append("iv", encrypted.iv);

  return encryptedForm;
};


// Function to get the token from localStorage
const getToken = () => localStorage.getItem(TOKEN_KEYS.JWT);
const getSessionId = () => localStorage.getItem("sessionId");

// List of APIs not requiring encryption
const openAPIs = [
  "/contact-us",
  "/user-register",
  "/send-otp",
  "/verify-otp",
  "/otp-verify",
  "/resend-otp",
  "/api/leads",
  "/api/eligibilty-calculator/get-all-requirements",
  "/api/eligibilty-calculator/calculate-eligibility",
  "/api/notify",
  "/ws/**"
];

// Axios instance
const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api-v1.dequitec.com/',
 
    // Ensure credentials are sent only when a token exists
    withCredentials: !!getToken() && process.env.REACT_APP_ENABLE_NETWORK_REQUESTS === 'true', // withCredentials will be true if the token exists and network requests are enabled
});

const adminInstance = axios.create({
  baseURL: process.env.REACT_APP_ADMIN_API_URL || 'https://admin-api-v1.dequitec.com',
  withCredentials: !!getToken() && process.env.REACT_APP_ENABLE_NETWORK_REQUESTS === 'true',
});

  // Allow CORS headers (optional in frontend)
  // instance.defaults.headers.post["Access-Control-Allow-Origin"] = "*";

  // REQUEST INTERCEPTOR
  instance.interceptors.request.use(
    async (config) => {
      const token = getToken();
      const sessionId = getSessionId()
      const isExternalUrl = typeof config.url === 'string' && /^(http|https):\/\//i.test(config.url);
      
      const isOpenAPI = openAPIs.some((api) =>
        config.url?.startsWith(api)
      );

      if (localStorage.getItem('sessionTimeoutInProgress') === 'true') {
        if (window._tokenCaptureTime && (Date.now() - window._tokenCaptureTime > 10000)) {
          localStorage.removeItem('sessionTimeoutInProgress');
        }
      }

      if (!isExternalUrl && token && !isOpenAPI) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (!isExternalUrl && sessionId) {
        config.headers['X-Session-Id'] = sessionId;
      }
      if (isExternalUrl) {
        config.withCredentials = false;
      }

      // Encrypt FormData
      if (!isExternalUrl && config.data instanceof FormData) {
        config.data = await encryptFormData(config.data);
      }
      // Encrypt JSON request body
      else if (!isExternalUrl && config.data) {
        config.data = encryptData(config.data);
      }

      // Encrypt URL query parameters
      if (!isExternalUrl && config.url?.includes("?")) {
        config.url = encryptUrlParams(config.url);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // RESPONSE INTERCEPTOR
  instance.interceptors.response.use(
    (response) => {
      const isOpenAPI = openAPIs.some((api) =>
        response.config.url?.startsWith(api)
      );
      const isExternalUrl = typeof response.config.url === 'string' && /^(http|https):\/\//i.test(response.config.url);
      void isOpenAPI;

      if (response.data && typeof response.data === 'object' && 'data' in response.data && 'iv' in response.data && !isExternalUrl) {
        response.data = decryptData(response.data);
      }

      return response;
    },
    (error) => {
      try {
        const response = error?.response;
        const isExternalUrl = typeof response?.config?.url === 'string' && /^(http|https):\/\//i.test(response.config.url);
        if (response?.data && !isExternalUrl) {
          response.data = decryptData(response.data);
        }
      } catch (e) {
        toast.error('Error decrypting response:', e);
      }
      return Promise.reject(error);
    }
  );

  adminInstance.interceptors.request.use(async (config) => {
    const token = getToken();
    const sessionId = getSessionId();
    const isExternalUrl = typeof config.url === 'string' && /^(http|https):\/\//i.test(config.url);

    const isPublishedBlogsEndpoint = config.url === '/api/blog/published' || config.url === 'api/blog/published';

    if (!isExternalUrl && token && !isPublishedBlogsEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (!isExternalUrl && sessionId) {
      config.headers['X-Session-Id'] = sessionId;
    }
    if (isExternalUrl) {
      config.withCredentials = false;
    }
    if (config.data instanceof FormData) {
      config.data = await encryptFormData(config.data);
    }
    // Encrypt JSON request body
    else if (config.data) {
      config.data = encryptData(config.data);
    }

    // Encrypt URL query parameters
    if (config.url?.includes("?")) {
      config.url = encryptUrlParams(config.url);
    }

    return config;
    // eslint-disable-next-line no-unreachable
    return config;
  }, (error) => {
    return Promise.reject(error);
  });

  adminInstance.interceptors.response.use(
    (response) => {
      const isOpenAPI = openAPIs.some((api) =>
        response.config.url?.startsWith(api)
      );
      const isExternalUrl = typeof response.config.url === 'string' && /^(http|https):\/\//i.test(response.config.url);
      void isOpenAPI;

      if (response.data && !isExternalUrl) {
        response.data = decryptData(response.data);
      }

      return response;
    },
    (error) => Promise.reject(error)
  );

  export { instance as default, adminInstance };
